import { useMemo } from 'react';
import { parentalConsentValidationMessages } from '@/Pages/ParentalConsent/copy';
import { validateCpf } from '@/Pages/Signup/utils/cpfValidation';
import type { ParentalConsentFormData } from '@/types/parental-consent';

const MIN_GUARDIAN_NAME_LENGTH = 2;
const MIN_PHONE_DIGITS = 10;
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export { parentalConsentValidationMessages };

export function validateParentalConsentField(
  field: keyof ParentalConsentFormData,
  form: ParentalConsentFormData,
): string | undefined {
  if (field === 'guardianFullName') {
    const normalized = form.guardianFullName.trim().replace(/\s+/g, ' ');
    if (normalized.length < MIN_GUARDIAN_NAME_LENGTH) {
      return parentalConsentValidationMessages.guardianFullName;
    }
    return undefined;
  }

  if (field === 'guardianEmail') {
    const email = form.guardianEmail.trim().toLowerCase();
    if (!EMAIL_PATTERN.test(email)) {
      return parentalConsentValidationMessages.guardianEmail;
    }
    return undefined;
  }

  if (field === 'guardianCpf') {
    const cpfResult = validateCpf(form.guardianCpf);
    if (!cpfResult.valid) {
      return cpfResult.message;
    }
    return undefined;
  }

  if (field === 'guardianPhone') {
    const digits = form.guardianPhone.replace(/\D/g, '');
    if (digits.length < MIN_PHONE_DIGITS) {
      return parentalConsentValidationMessages.guardianPhone;
    }
    return undefined;
  }

  if (field === 'guardianRelationship') {
    if (!form.guardianRelationship) {
      return parentalConsentValidationMessages.guardianRelationship;
    }
    return undefined;
  }

  if (field === 'consentGiven') {
    if (!form.consentGiven) {
      return parentalConsentValidationMessages.consentGiven;
    }
    return undefined;
  }

  return undefined;
}

export function isParentalConsentFormValid(form: ParentalConsentFormData): boolean {
  const fields: (keyof ParentalConsentFormData)[] = [
    'guardianFullName',
    'guardianEmail',
    'guardianCpf',
    'guardianPhone',
    'guardianRelationship',
    'consentGiven',
  ];
  return fields.every((field) => validateParentalConsentField(field, form) === undefined);
}

export type UseParentalConsentValidationResult = {
  validateField: (
    field: keyof ParentalConsentFormData,
    form: ParentalConsentFormData,
  ) => string | undefined;
  isFormValid: (form: ParentalConsentFormData) => boolean;
};

export function useParentalConsentValidation(): UseParentalConsentValidationResult {
  return useMemo(
    () => ({
      validateField: validateParentalConsentField,
      isFormValid: isParentalConsentFormValid,
    }),
    [],
  );
}

export function createInitialParentalConsentForm(): ParentalConsentFormData {
  return {
    guardianFullName: '',
    guardianEmail: '',
    guardianCpf: '',
    guardianPhone: '',
    guardianRelationship: '',
    consentGiven: false,
  };
}
